import React, { Component } from "react";
import {
  Row, Col, Form, Button, Card, Badge, Spinner, Offcanvas, Table,
  ProgressBar, Nav,
} from "react-bootstrap";
import { toast } from "react-toastify";
import configData from "../config.json";
import LoadingOverlay from "./LoadingOverlay";
import {
  extractPageConfig,
  toWidgetInstanceConfig,
  generateNewPageContent,
} from "../widgets/migration/PageExtractor";

/**
 * BatchMigration - Admin page for migrating existing pages to the widget framework.
 * Two modes: Single (paste source) and Bulk (upload multiple .js files).
 * Class component per project convention.
 */
export default class BatchMigration extends Component {
  constructor(props) {
    super(props);
    this.fileInputRef = React.createRef();
    this.state = {
      loading: false,
      loadingMessage: "Processing your request...",
      sId: sessionStorage.getItem("sessionId"),
      uId: sessionStorage.getItem("uId"),
      activeTab: "single",
      // --- Single mode ---
      pageName: "",
      pageRoute: "",
      sourceCode: "",
      extracted: null,
      widgetInstanceConfig: null,
      newPageCode: "",
      // --- Bulk mode ---
      bulkFiles: [],       // { fileName, pageName, pageRoute, sourceCode, extracted, widgetInstanceConfig, status }
      bulkMigrating: false,
      bulkProgress: 0,
      bulkTotal: 0,
      // --- Shared ---
      migrating: false,
      migrationLog: [],
      existingInstances: [],
      existingLayouts: [],
      loadingExisting: true,
      showCodePanel: false,
      // Generated Header.js code for bulk
      bulkHeaderCode: "",
      showBulkCodePanel: false,
    };
  }

  componentDidMount() {
    this.fetchExistingData();
  }

  fetchExistingData = () => {
    this.setState({ loadingExisting: true });

    const formData = new FormData();
    formData.append("sId", this.state.sId);
    formData.append("uId", this.state.uId);

    fetch(configData.api_url + "C_Widget_instances/list", {
      method: "POST",
      mode: "cors",
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          this.setState({ existingInstances: data.list || [] });
        }
      })
      .catch(() => {});

    const formData2 = new FormData();
    formData2.append("sId", this.state.sId);
    formData2.append("uId", this.state.uId);

    fetch(configData.api_url + "C_Page_layouts/list", {
      method: "POST",
      mode: "cors",
      body: formData2,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          this.setState({ existingLayouts: data.list || [], loadingExisting: false });
        } else {
          this.setState({ loadingExisting: false });
        }
      })
      .catch(() => this.setState({ loadingExisting: false }));
  };

  // ==================== SHARED HELPERS ====================

  addLog = (msg) => {
    this.setState((prev) => ({
      migrationLog: [...prev.migrationLog, { time: new Date().toLocaleTimeString(), msg }],
    }));
  };

  postFormData = (endpoint, fields) => {
    const formData = new FormData();
    formData.append("sId", this.state.sId);
    formData.append("uId", this.state.uId);
    Object.entries(fields).forEach(([k, v]) => formData.append(k, v));

    return fetch(configData.api_url + endpoint, {
      method: "POST",
      mode: "cors",
      body: formData,
    }).then((res) => res.json());
  };

  isAlreadyMigrated = (route) => {
    return this.state.existingLayouts.some((l) => l.pageRoute === route);
  };

  /**
   * Core migration logic — creates all DB records for one page.
   * Returns true on success, false on failure.
   */
  migrateOnePage = async (pageName, pageRoute, extracted, widgetInstanceConfig) => {
    try {
      // Step 1: Create widget instance
      this.addLog(`[${pageName}] Creating widget instance...`);
      const wiData = await this.postFormData("C_Widget_instances/add", {
        instanceName: widgetInstanceConfig.instanceName,
        widget_types_Id: widgetInstanceConfig.widget_types_Id,
        apiEndPoint: widgetInstanceConfig.apiEndPoint,
        apiMethod: widgetInstanceConfig.apiMethod || "POST",
        apiParams: widgetInstanceConfig.apiParams || "",
        columnsConfig: widgetInstanceConfig.columnsConfig,
        actionsConfig: widgetInstanceConfig.actionsConfig,
        tableConfig: widgetInstanceConfig.tableConfig,
        formConfig: widgetInstanceConfig.formConfig,
        chartConfig: widgetInstanceConfig.chartConfig || "",
        filterConfig: widgetInstanceConfig.filterConfig || "",
        customProps: widgetInstanceConfig.customProps || "",
        sourceTable: widgetInstanceConfig.sourceTable,
        isActive: 1,
      });

      if (wiData.status !== "success") {
        this.addLog(`[${pageName}] FAILED: Widget instance - ${wiData.message || "Unknown"}`);
        return false;
      }

      // Step 2: Create page layout
      this.addLog(`[${pageName}] Creating page layout...`);
      const plData = await this.postFormData("C_Page_layouts/add", {
        pageRoute: pageRoute,
        pageTitle: extracted.pageTitle || pageName,
        pageIcon: "",
        layoutType: "standard",
        layoutConfig: "{}",
        isActive: 1,
      });

      if (plData.status !== "success") {
        this.addLog(`[${pageName}] FAILED: Page layout - ${plData.message || "Unknown"}`);
        return false;
      }

      // Step 3: Lookup IDs
      const wiListData = await this.postFormData("C_Widget_instances/list", {});
      let widgetInstanceId = null;
      if (wiListData.status === "success" && wiListData.list) {
        const wi = wiListData.list.find((w) => w.instanceName === widgetInstanceConfig.instanceName);
        if (wi) widgetInstanceId = wi.widget_instances_Id;
      }

      const plListData = await this.postFormData("C_Page_layouts/list", {});
      let pageLayoutId = null;
      if (plListData.status === "success" && plListData.list) {
        const pl = plListData.list.find((p) => p.pageRoute === pageRoute);
        if (pl) pageLayoutId = pl.page_layouts_Id;
      }

      if (!pageLayoutId) {
        this.addLog(`[${pageName}] WARNING: Could not find page layout ID`);
        return false;
      }

      // Step 4: Create layout row
      const lrData = await this.postFormData("C_Layout_rows/add", {
        page_layouts_Id: pageLayoutId,
        rowOrder: 1,
        rowHeight: "auto",
        cssClass: "",
        isActive: 1,
      });

      if (lrData.status !== "success") {
        this.addLog(`[${pageName}] FAILED: Layout row - ${lrData.message || "Unknown"}`);
        return false;
      }

      // Step 5: Lookup row ID and create cell
      const lrListData = await this.postFormData("C_Layout_rows/list", {});
      let layoutRowId = null;
      if (lrListData.status === "success" && lrListData.list) {
        const lr = lrListData.list.find((r) => String(r.page_layouts_Id) === String(pageLayoutId));
        if (lr) layoutRowId = lr.layout_rows_Id;
      }

      if (layoutRowId && widgetInstanceId) {
        await this.postFormData("C_Layout_cells/add", {
          layout_rows_Id: layoutRowId,
          cellOrder: 1,
          colSpan: 12,
          widget_instances_Id: widgetInstanceId,
          cssClass: "",
          isActive: 1,
        });
      }

      // Step 6: Feature flag
      await this.postFormData("C_User_feature_flags/add", {
        uId: 1,
        featureKey: "widget_" + pageRoute,
        isEnabled: 1,
        routePath: pageRoute,
        newComponent: pageName + "_NEW",
        description: "",
      });

      this.addLog(`[${pageName}] COMPLETE`);
      return true;
    } catch (err) {
      this.addLog(`[${pageName}] ERROR: ${err.message}`);
      return false;
    }
  };

  // ==================== SINGLE MODE ====================

  handleParse = () => {
    const { pageName, sourceCode } = this.state;
    if (!pageName.trim()) { toast.error("Page Name is required"); return; }
    if (!sourceCode.trim()) { toast.error("Paste the source code first"); return; }

    const extracted = extractPageConfig(sourceCode, pageName.trim());
    const widgetInstanceConfig = toWidgetInstanceConfig(extracted);
    const pageRoute = this.state.pageRoute || pageName.trim().toLowerCase();
    const newPageCode = generateNewPageContent(pageName.trim(), pageRoute);

    this.setState({ extracted, widgetInstanceConfig, newPageCode, pageRoute });
    toast.success("Config extracted successfully");
  };

  handleMigrate = async () => {
    const { widgetInstanceConfig, pageRoute, pageName, extracted } = this.state;
    if (!widgetInstanceConfig || !pageRoute) { toast.error("Parse the source code first"); return; }

    this.setState({ migrating: true, migrationLog: [] });
    const success = await this.migrateOnePage(pageName, pageRoute, extracted, widgetInstanceConfig);
    if (success) {
      toast.success("Migration completed for " + pageName);
      this.fetchExistingData();
    } else {
      toast.error("Migration failed for " + pageName);
    }
    this.setState({ migrating: false });
  };

  // ==================== BULK MODE ====================

  handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const readers = files.map((file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (ev) => {
          const fileName = file.name;
          // Derive pageName from filename: "Industrytypemaster.js" → "Industrytypemaster"
          const pageName = fileName.replace(/\.js$/, "");
          const pageRoute = pageName.toLowerCase();
          const sourceCode = ev.target.result;

          // Skip _NEW files, Common.js, index files, etc.
          if (fileName.endsWith("_NEW.js") || fileName === "Common.js" ||
              fileName === "index.js" || fileName.startsWith(".")) {
            resolve(null);
            return;
          }

          // Try to extract config
          let extracted = null;
          let widgetInstanceConfig = null;
          let status = "pending";

          try {
            extracted = extractPageConfig(sourceCode, pageName);
            widgetInstanceConfig = toWidgetInstanceConfig(extracted);

            // Mark quality
            if (extracted.columns.length === 0 && extracted.formFields.length === 0) {
              status = "skip"; // No extractable config — likely not a standard CRUD page
            } else if (this.isAlreadyMigrated(pageRoute)) {
              status = "migrated";
            }
          } catch (err) {
            status = "error";
          }

          resolve({
            fileName, pageName, pageRoute, sourceCode,
            extracted, widgetInstanceConfig, status,
            columns: extracted ? extracted.columns.length : 0,
            fields: extracted ? extracted.formFields.length : 0,
            endpoints: extracted ? Object.keys(extracted.apiEndpoints).length : 0,
          });
        };
        reader.readAsText(file);
      });
    });

    Promise.all(readers).then((results) => {
      const bulkFiles = results.filter(Boolean);
      // Sort: pending first, then migrated, then skip/error
      bulkFiles.sort((a, b) => {
        const order = { pending: 0, migrated: 1, skip: 2, error: 3 };
        return (order[a.status] || 9) - (order[b.status] || 9);
      });
      this.setState({ bulkFiles });
      toast.success(`Parsed ${bulkFiles.length} files (${bulkFiles.filter(f => f.status === "pending").length} ready to migrate)`);
    });
  };

  handleBulkToggle = (index) => {
    const bulkFiles = [...this.state.bulkFiles];
    const f = bulkFiles[index];
    if (f.status === "pending") f.status = "skip";
    else if (f.status === "skip" && f.columns > 0) f.status = "pending";
    bulkFiles[index] = f;
    this.setState({ bulkFiles });
  };

  handleBulkMigrate = async () => {
    const { bulkFiles } = this.state;
    const toMigrate = bulkFiles.filter((f) => f.status === "pending");
    if (toMigrate.length === 0) { toast.error("No pages selected for migration"); return; }

    this.setState({ bulkMigrating: true, migrationLog: [], bulkProgress: 0, bulkTotal: toMigrate.length });

    let successCount = 0;
    let failCount = 0;
    const updatedFiles = [...bulkFiles];

    for (let i = 0; i < toMigrate.length; i++) {
      const f = toMigrate[i];
      const idx = updatedFiles.findIndex((u) => u.fileName === f.fileName);
      this.setState({ bulkProgress: i + 1 });

      const success = await this.migrateOnePage(f.pageName, f.pageRoute, f.extracted, f.widgetInstanceConfig);
      if (success) {
        updatedFiles[idx] = { ...updatedFiles[idx], status: "migrated" };
        successCount++;
      } else {
        updatedFiles[idx] = { ...updatedFiles[idx], status: "error" };
        failCount++;
      }
      this.setState({ bulkFiles: updatedFiles });
    }

    // Generate bulk Header.js code and _NEW.js files
    const migrated = updatedFiles.filter((f) => f.status === "migrated");
    const imports = migrated.map((f) => `import ${f.pageName}_NEW from "../Sidebar/${f.pageName}_NEW";`).join("\n");
    const mapEntries = migrated.map((f) => `    ${f.pageRoute}: ${f.pageName}_NEW,`).join("\n");
    const routes = migrated.map((f) =>
      `                      {(() => {\n                        const C = this.resolveComponent("${f.pageRoute}", ${f.pageName});\n                        return <Route path="${f.pageRoute}" element={<C />}></Route>;\n                      })()}`
    ).join("\n");

    const bulkHeaderCode = `// === IMPORTS (add at top of Header.js) ===\n${imports}\n\n// === ADD TO widgetNewComponents MAP ===\n${mapEntries}\n\n// === ROUTE REPLACEMENTS ===\n${routes}`;

    this.setState({ bulkMigrating: false, bulkHeaderCode });
    this.fetchExistingData();
    toast.success(`Bulk migration: ${successCount} succeeded, ${failCount} failed`);
  };

  generateBulkNewFiles = () => {
    const migrated = this.state.bulkFiles.filter((f) => f.status === "migrated");
    return migrated.map((f) => ({
      fileName: `${f.pageName}_NEW.js`,
      content: generateNewPageContent(f.pageName, f.pageRoute),
    }));
  };

  // ==================== RENDER ====================

  renderMigrationLog = () => {
    const { migrationLog } = this.state;
    if (migrationLog.length === 0) return null;

    return (
      <Card className="mb-3">
        <Card.Header className="py-1 px-2 d-flex justify-content-between">
          <span style={{ fontSize: "0.85rem" }}>
            <i className="bi bi-journal-text me-1"></i> Migration Log
          </span>
          <Button variant="outline-secondary" size="sm" style={{ fontSize: "0.7rem", padding: "0 6px" }}
            onClick={() => this.setState({ migrationLog: [] })}>Clear</Button>
        </Card.Header>
        <Card.Body style={{ maxHeight: "200px", overflowY: "auto", fontFamily: "monospace", fontSize: "0.75rem", backgroundColor: "#1e1e1e", color: "#d4d4d4" }}>
          {migrationLog.map((log, i) => (
            <div key={i}>
              <span style={{ color: "#569cd6" }}>[{log.time}]</span>{" "}
              <span style={{ color: log.msg.includes("FAILED") ? "#f44747" : log.msg.includes("WARNING") ? "#dcdcaa" : log.msg.includes("COMPLETE") ? "#6a9955" : "#d4d4d4" }}>
                {log.msg}
              </span>
            </div>
          ))}
        </Card.Body>
      </Card>
    );
  };

  renderExistingMigrations = () => {
    const { existingLayouts, loadingExisting } = this.state;
    return (
      <Card>
        <Card.Header className="py-1 px-2" style={{ fontSize: "0.85rem" }}>
          <i className="bi bi-check-circle me-1"></i> Migrated Pages <Badge bg="success">{existingLayouts.length}</Badge>
        </Card.Header>
        <Card.Body style={{ maxHeight: "200px", overflowY: "auto" }}>
          {loadingExisting ? (
            <div className="text-center py-2"><Spinner size="sm" /> Loading...</div>
          ) : existingLayouts.length === 0 ? (
            <div className="text-muted text-center py-2" style={{ fontSize: "0.85rem" }}>No pages migrated yet</div>
          ) : (
            <Table size="sm" bordered hover style={{ fontSize: "0.8rem" }}>
              <thead><tr><th>Route</th><th>Title</th></tr></thead>
              <tbody>
                {existingLayouts.map((l) => (
                  <tr key={l.page_layouts_Id}>
                    <td><code>{l.pageRoute}</code></td>
                    <td>{l.pageTitle}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    );
  };

  renderSingleMode = () => {
    const { pageName, pageRoute, sourceCode, extracted, widgetInstanceConfig, newPageCode, migrating, showCodePanel } = this.state;

    return (
      <Row>
        <Col md={6}>
          <Card className="mb-3">
            <Card.Header><i className="bi bi-code-slash me-1"></i> Source Code Input</Card.Header>
            <Card.Body>
              <Row className="mb-2">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Page Name (PascalCase)</Form.Label>
                    <Form.Control type="text" value={pageName}
                      onChange={(e) => this.setState({ pageName: e.target.value })}
                      placeholder="e.g., Industrytypemaster" />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Page Route</Form.Label>
                    <Form.Control type="text" value={pageRoute}
                      onChange={(e) => this.setState({ pageRoute: e.target.value })}
                      placeholder="auto from page name" />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-2">
                <Form.Label>
                  Paste Page Source Code
                  {sourceCode && <Badge bg="info" className="ms-2">{sourceCode.split("\n").length} lines</Badge>}
                </Form.Label>
                <Form.Control as="textarea" rows={10} value={sourceCode}
                  onChange={(e) => this.setState({ sourceCode: e.target.value })}
                  placeholder="Paste the contents of the existing .js page file here..."
                  style={{ fontFamily: "monospace", fontSize: "0.8rem" }} />
              </Form.Group>
              <div className="d-flex gap-2">
                <Button variant="primary" onClick={this.handleParse} disabled={!pageName || !sourceCode}>
                  <i className="bi bi-gear me-1"></i> Parse & Extract
                </Button>
                <Button variant="outline-secondary" onClick={() => this.setState({
                  pageName: "", pageRoute: "", sourceCode: "", extracted: null,
                  widgetInstanceConfig: null, newPageCode: "", migrationLog: [],
                })}>Clear</Button>
              </div>
            </Card.Body>
          </Card>
          {this.renderMigrationLog()}
        </Col>

        <Col md={6}>
          {extracted && (
            <Card className="mb-3">
              <Card.Header className="d-flex justify-content-between">
                <span><i className="bi bi-clipboard-data me-1"></i> Extracted Config</span>
                {this.isAlreadyMigrated(pageRoute) && <Badge bg="warning">Already Migrated</Badge>}
              </Card.Header>
              <Card.Body>
                <Table size="sm" bordered style={{ fontSize: "0.85rem" }}>
                  <tbody>
                    <tr><td><strong>Table</strong></td><td>{extracted.tableName}</td><td><strong>ID Field</strong></td><td><code>{extracted.idField}</code></td></tr>
                    <tr><td><strong>Columns</strong></td><td>{extracted.columns.length}</td><td><strong>Form Fields</strong></td><td>{extracted.formFields.length}</td></tr>
                    <tr><td><strong>Endpoints</strong></td><td colSpan="3">{Object.entries(extracted.apiEndpoints).map(([k, v]) => <span key={k} className="me-2"><Badge bg="secondary">{k}</Badge> {v}</span>)}</td></tr>
                  </tbody>
                </Table>

                <div className="d-flex gap-2 mt-2">
                  <Button variant="success" onClick={this.handleMigrate}
                    disabled={migrating || this.isAlreadyMigrated(pageRoute)}>
                    {migrating ? <><Spinner size="sm" className="me-1" /> Migrating...</> : <><i className="bi bi-cloud-upload me-1"></i> Create DB Records</>}
                  </Button>
                  <Button variant="outline-info" onClick={() => this.setState({ showCodePanel: true })}>
                    <i className="bi bi-file-code me-1"></i> View Code
                  </Button>
                </div>
              </Card.Body>
            </Card>
          )}
          {this.renderExistingMigrations()}
        </Col>

        {/* Code Panel */}
        <Offcanvas show={showCodePanel} onHide={() => this.setState({ showCodePanel: false })} placement="end" style={{ width: "600px" }}>
          <Offcanvas.Header closeButton>
            <Offcanvas.Title>Generated {pageName}_NEW.js</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <p className="text-muted" style={{ fontSize: "0.85rem" }}>
              Create at <code>src/Sidebar/{pageName}_NEW.js</code>:
            </p>
            <pre style={{ backgroundColor: "#1e1e1e", color: "#d4d4d4", padding: "12px", borderRadius: "6px", fontSize: "0.8rem" }}>{newPageCode}</pre>
            <Button variant="outline-primary" size="sm" className="mb-3" onClick={() => { navigator.clipboard.writeText(newPageCode); toast.success("Copied!"); }}>
              <i className="bi bi-clipboard me-1"></i> Copy
            </Button>
            <hr />
            <p className="text-muted" style={{ fontSize: "0.85rem" }}>Add to Header.js:</p>
            <pre style={{ backgroundColor: "#1e1e1e", color: "#d4d4d4", padding: "12px", borderRadius: "6px", fontSize: "0.8rem" }}>
{`import ${pageName}_NEW from "../Sidebar/${pageName}_NEW";

// widgetNewComponents:
${pageRoute}: ${pageName}_NEW,

// Route:
{(() => {
  const C = this.resolveComponent("${pageRoute}", ${pageName});
  return <Route path="${pageRoute}" element={<C />}></Route>;
})()}`}
            </pre>
            <Button variant="outline-primary" size="sm" onClick={() => {
              const code = `import ${pageName}_NEW from "../Sidebar/${pageName}_NEW";\n\n// widgetNewComponents:\n${pageRoute}: ${pageName}_NEW,\n\n// Route:\n{(() => {\n  const C = this.resolveComponent("${pageRoute}", ${pageName});\n  return <Route path="${pageRoute}" element={<C />}></Route>;\n})()}`;
              navigator.clipboard.writeText(code); toast.success("Header.js code copied!");
            }}><i className="bi bi-clipboard me-1"></i> Copy Header.js Code</Button>
          </Offcanvas.Body>
        </Offcanvas>
      </Row>
    );
  };

  renderBulkMode = () => {
    const { bulkFiles, bulkMigrating, bulkProgress, bulkTotal, bulkHeaderCode, showBulkCodePanel } = this.state;
    const pendingCount = bulkFiles.filter((f) => f.status === "pending").length;
    const migratedCount = bulkFiles.filter((f) => f.status === "migrated").length;
    const skipCount = bulkFiles.filter((f) => f.status === "skip").length;
    const errorCount = bulkFiles.filter((f) => f.status === "error").length;

    return (
      <Row>
        <Col md={12}>
          {/* Upload Area */}
          <Card className="mb-3">
            <Card.Header><i className="bi bi-upload me-1"></i> Upload Page Files</Card.Header>
            <Card.Body>
              <p style={{ fontSize: "0.85rem" }} className="text-muted mb-2">
                Select all <code>.js</code> files from <code>src/Sidebar/</code>. The tool will auto-detect standard CRUD pages and extract their config.
                Files like _NEW.js, Common.js, and non-CRUD pages will be skipped.
              </p>
              <div className="d-flex gap-2 align-items-center">
                <input type="file" ref={this.fileInputRef} multiple accept=".js"
                  onChange={this.handleFileUpload} style={{ display: "none" }} />
                <Button variant="primary" onClick={() => this.fileInputRef.current.click()}>
                  <i className="bi bi-folder2-open me-1"></i> Select Files
                </Button>
                {bulkFiles.length > 0 && (
                  <>
                    <Badge bg="primary">{pendingCount} ready</Badge>
                    <Badge bg="success">{migratedCount} migrated</Badge>
                    <Badge bg="secondary">{skipCount} skipped</Badge>
                    {errorCount > 0 && <Badge bg="danger">{errorCount} errors</Badge>}
                    <Button variant="success" onClick={this.handleBulkMigrate}
                      disabled={bulkMigrating || pendingCount === 0} className="ms-auto">
                      {bulkMigrating ? (
                        <><Spinner size="sm" className="me-1" /> Migrating {bulkProgress}/{bulkTotal}...</>
                      ) : (
                        <><i className="bi bi-cloud-upload me-1"></i> Migrate {pendingCount} Pages</>
                      )}
                    </Button>
                    {migratedCount > 0 && (
                      <Button variant="outline-info" onClick={() => this.setState({ showBulkCodePanel: true })}>
                        <i className="bi bi-file-code me-1"></i> View All Generated Code
                      </Button>
                    )}
                  </>
                )}
              </div>
              {bulkMigrating && (
                <ProgressBar now={(bulkProgress / bulkTotal) * 100} className="mt-2"
                  label={`${bulkProgress}/${bulkTotal}`} animated striped />
              )}
            </Card.Body>
          </Card>

          {/* File List */}
          {bulkFiles.length > 0 && (
            <Card className="mb-3">
              <Card.Header className="py-1 px-2" style={{ fontSize: "0.85rem" }}>
                <i className="bi bi-list-check me-1"></i> Parsed Files ({bulkFiles.length})
              </Card.Header>
              <Card.Body style={{ maxHeight: "400px", overflowY: "auto", padding: "0" }}>
                <Table size="sm" bordered hover style={{ fontSize: "0.8rem", marginBottom: 0 }}>
                  <thead style={{ position: "sticky", top: 0, backgroundColor: "#fff" }}>
                    <tr>
                      <th style={{ width: "40px" }}></th>
                      <th>File</th>
                      <th>Page Name</th>
                      <th>Route</th>
                      <th style={{ width: "60px" }}>Cols</th>
                      <th style={{ width: "60px" }}>Fields</th>
                      <th style={{ width: "60px" }}>APIs</th>
                      <th style={{ width: "90px" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkFiles.map((f, i) => (
                      <tr key={i} style={{ opacity: f.status === "skip" ? 0.5 : 1 }}>
                        <td className="text-center">
                          {(f.status === "pending" || (f.status === "skip" && f.columns > 0)) && (
                            <Form.Check type="checkbox" checked={f.status === "pending"}
                              onChange={() => this.handleBulkToggle(i)} />
                          )}
                        </td>
                        <td><code>{f.fileName}</code></td>
                        <td>{f.pageName}</td>
                        <td><code>{f.pageRoute}</code></td>
                        <td className="text-center">{f.columns}</td>
                        <td className="text-center">{f.fields}</td>
                        <td className="text-center">{f.endpoints}</td>
                        <td>
                          <Badge bg={
                            f.status === "pending" ? "primary" :
                            f.status === "migrated" ? "success" :
                            f.status === "error" ? "danger" : "secondary"
                          } style={{ fontSize: "0.7rem" }}>{f.status}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}

          {this.renderMigrationLog()}
          {this.renderExistingMigrations()}
        </Col>

        {/* Bulk Code Panel */}
        <Offcanvas show={showBulkCodePanel} onHide={() => this.setState({ showBulkCodePanel: false })} placement="end" style={{ width: "700px" }}>
          <Offcanvas.Header closeButton>
            <Offcanvas.Title>Generated Code for All Migrated Pages</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <h6>1. Header.js Changes</h6>
            <p className="text-muted" style={{ fontSize: "0.8rem" }}>Copy and add to Header.js (imports at top, map entries in widgetNewComponents, routes in the Routes section):</p>
            <pre style={{ backgroundColor: "#1e1e1e", color: "#d4d4d4", padding: "12px", borderRadius: "6px", fontSize: "0.75rem", maxHeight: "300px", overflowY: "auto" }}>
              {bulkHeaderCode}
            </pre>
            <Button variant="outline-primary" size="sm" className="mb-3" onClick={() => {
              navigator.clipboard.writeText(bulkHeaderCode); toast.success("Header.js code copied!");
            }}><i className="bi bi-clipboard me-1"></i> Copy Header.js Code</Button>

            <hr />
            <h6>2. _NEW.js Files to Create</h6>
            <p className="text-muted" style={{ fontSize: "0.8rem" }}>Create each of these files in <code>src/Sidebar/</code>:</p>
            {this.generateBulkNewFiles().map((f, i) => (
              <div key={i} className="mb-2">
                <div className="d-flex justify-content-between align-items-center">
                  <code style={{ fontSize: "0.8rem" }}>{f.fileName}</code>
                  <Button variant="outline-primary" size="sm" style={{ fontSize: "0.7rem", padding: "0 6px" }}
                    onClick={() => { navigator.clipboard.writeText(f.content); toast.success(`${f.fileName} copied!`); }}>
                    Copy
                  </Button>
                </div>
                <pre style={{ backgroundColor: "#1e1e1e", color: "#d4d4d4", padding: "8px", borderRadius: "4px", fontSize: "0.7rem" }}>{f.content}</pre>
              </div>
            ))}
          </Offcanvas.Body>
        </Offcanvas>
      </Row>
    );
  };

  render() {
    const { activeTab } = this.state;

    return (
      <div className="page-title">
        <LoadingOverlay show={this.state.loading} message={this.state.loadingMessage} />
        <div className="row">
          <div className="col-md-12">
            <div className="x_panel">
              <div className="x_content">
                <h3 className="logoskybluecolor" style={{ textAlign: "start" }}>
                  <i className="bi bi-arrow-repeat me-2"></i>
                  Batch Migration Tool
                </h3>

                <Nav variant="tabs" className="mb-3" activeKey={activeTab}
                  onSelect={(k) => this.setState({ activeTab: k })}>
                  <Nav.Item>
                    <Nav.Link eventKey="single">
                      <i className="bi bi-file-earmark-code me-1"></i> Single Page
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="bulk">
                      <i className="bi bi-files me-1"></i> Bulk Upload
                    </Nav.Link>
                  </Nav.Item>
                </Nav>

                {activeTab === "single" ? this.renderSingleMode() : this.renderBulkMode()}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
